import { BaseModal } from './BaseModal'
import { User } from '../../lib/user'
import { getPuzzle } from '../../lib/words'
import { Puzzle } from '../../components/puzzle/Puzzle'
import { useState, useRef, useEffect, useCallback } from 'react'
import { Shape as ShapeData } from '../../lib/shapes'
import { randomSeed } from '../../lib/rand'
import { useAlert } from '../../context/AlertContext'
import { RefreshIcon, ShareIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/outline'
import { useAchievements } from '../../context/AchievementsContext'
import { copyTextToClipboard } from '../../lib/clipboard'
import { DAILY_LIST, CustomURLData } from '../../lib/words'
import { GameStats } from '../../lib/localStorage'
import Filter from 'bad-words'
import { useNavigate } from 'react-router-dom'

type Props = {
  user: User,
  isOpen: boolean,
  isDarkMode: boolean,
  handleClose: () => void
}

const filter = new Filter();

export const CustomGameModal = ({ user, isOpen, isDarkMode, handleClose }: Props) => {
  const navigate = useNavigate()
  const {grantKeyedAchievement} = useAchievements() 
  const {showSuccess} = useAlert()
  const [solution, setSolution] = useState("")
  const [title, setTitle] = useState("")
  const [hint, setHint] = useState("")
  const [creator, setCreator] = useState("")
  const [puzzle, setPuzzle] = useState<ShapeData[]>([])
  const [seed, setSeed] = useState(randomSeed())
  const [error, setError] = useState("")
  const [warning, setWarning] = useState("")
  const [advanced, setAdvanced] = useState(false)
  const solutionInput = useRef<HTMLInputElement>(null);

  const solutionChange = (event: any) => {
    setSolution(event.target.value.toLowerCase());
  }

  const titleChange = (event: any) => {
    setTitle(event.target.value);
  }

  const hintChange = (event: any) => {
    setHint(event.target.value);
  }

  const creatorChange = (event: any) => {
    setCreator(event.target.value);
  }

  const preventNonLetters = (event: any) => {
    if (!/[a-zA-Z]/.test(event.key)) {
      event.preventDefault();
    }
  }

  const toggleAdvanced = () => {
    setAdvanced(!advanced);
  }

  const onPreview = useCallback(() => {
    if(!solutionInput.current) {
      return false
    }
    const newSolution = solutionInput.current.value;
    if(newSolution.length > 8 || newSolution.length < 5) {
      setError("Custom words must be 5 to 8 characters long")
      setWarning("")
      return false
    }
    if(new Set([...newSolution]).size > 7) {
      setError("8 letter words must have at least 1 repeating letter")
      setWarning("")
      return false
    }

    if(filter.isProfane(newSolution)) {
      setError("This word is not allowed")
      setWarning("")
      return false
    }

    if(!DAILY_LIST.isWordInWordList(newSolution)) {
      setWarning("This word is not in our dictionary")
    } else {
      setWarning("")
    }

    setError("")
    setPuzzle(getPuzzle(newSolution, seed));
    return true
  }, [seed]);

  const onRefresh = () => {
    const validated = onPreview()
    if(!validated || solutionInput.current?.value !== solution) {
      return
    }
    const newSeed = randomSeed()
    setSeed(newSeed)
    setPuzzle(getPuzzle(solution, newSeed));
  }

  const slug = () => {
    const id: CustomURLData = {
      word: solution.toUpperCase(),
      seed,
      title,
      creator,
      hint,
    }

    return `/custom/${btoa(JSON.stringify(id))}/${seed}`
  }

  const url = () => {
    return 'https://www.polygonle.com' + slug()
  }

  const onShare = () => {
    copyTextToClipboard(url())
    showSuccess("Copied custom puzzle url to your clipboard!")
    grantKeyedAchievement(user, "designer")
  }

  const onOpen = () => {
    handleClose()
    navigate(slug())
    grantKeyedAchievement(user, "designer")
  }

  useEffect(() => {
    if(!isOpen) {
      return
    }
    const listener = (e: KeyboardEvent) => {
      const isEnter = e.key === 'Enter' || e.keyCode === 13;
      if(isEnter) {
        onPreview();
      }
    }
    window.addEventListener('keydown', listener)
    return () => {
      window.removeEventListener('keydown', listener)
    }
  }, [isOpen, onPreview])


  // TODO: expand dictionaries to include shorter words
  return (
      <BaseModal title="Custom puzzle" isOpen={isOpen} handleClose={handleClose}>
        <div className="flex flex-col justify-center items-center">
          <p className="text-sm text-gray-700 dark:text-gray-300 p-2">What is your secret word? </p>
          {error && <p className="text-sm text-red-500">{error}</p>}
          {warning && <p className="text-sm text-yellow-500">Warning: {warning}</p>}
          <input className={`bg-gray-50 border ${error ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} text-gray-900 text-sm rounded-lg focus:ring-indigo-500 focus:border-indigo-500 block m-4 p-2.5 dark:bg-gray-700 dark:placeholder-gray-400 dark:text-white dark:focus:ring-indigo-500 dark:focus:border-indigo-500`} ref={solutionInput} onKeyPress={preventNonLetters} onChange={solutionChange} value={solution} />
          {advanced && <>
            <p onClick={toggleAdvanced} className="flex items-center justify-center text-sm text-gray-700 dark:text-gray-300">Advanced Options<ChevronUpIcon className="h-6 w-6 ml-2 cursor-pointer dark:stroke-white" /></p>
            <div className="m-2">
              <label className="block text-sm text-gray-700 dark:text-gray-300" htmlFor="title">Title</label>
              <input id="title" className="bg-gray-50 border border-gray-300 dark:border-gray-600 text-gray-900 text-sm rounded-lg focus:ring-indigo-500 focus:border-indigo-500 block mt-2 p-2.5 dark:bg-gray-700 dark:placeholder-gray-400 dark:text-white dark:focus:ring-indigo-500 dark:focus:border-indigo-500" onChange={titleChange} value={title} placeholder="(optional)" />
            </div>
            <div className="mb-2">
              <label className="block text-sm text-gray-700 dark:text-gray-300" htmlFor="hint">Hint</label>
              <input id="hint" className="bg-gray-50 border border-gray-300 dark:border-gray-600 text-gray-900 text-sm rounded-lg focus:ring-indigo-500 focus:border-indigo-500 block mt-2 p-2.5 dark:bg-gray-700 dark:placeholder-gray-400 dark:text-white dark:focus:ring-indigo-500 dark:focus:border-indigo-500" onChange={hintChange} value={hint} placeholder="(optional)"/>
            </div>
            <div className="mb-2">
              <label className="block text-sm text-gray-700 dark:text-gray-300" htmlFor="creator">Creator</label>
              <input id="creator" className="bg-gray-50 border border-gray-300 dark:border-gray-600 text-gray-900 text-sm rounded-lg focus:ring-indigo-500 focus:border-indigo-500 block mt-2 p-2.5 dark:bg-gray-700 dark:placeholder-gray-400 dark:text-white dark:focus:ring-indigo-500 dark:focus:border-indigo-500" onChange={creatorChange} value={creator} placeholder="(optional)" />
            </div>
            </>
          }
          {!advanced &&  
            <p onClick={toggleAdvanced}className="flex items-center justify-center text-sm text-gray-700 dark:text-gray-300">Advanced Options<ChevronDownIcon className="h-6 w-6 ml-2 cursor-pointer dark:stroke-white" /></p>
          }
          <div className="flex flex-row items-center p-4">
            <button className="w-32 justify-center items-center text-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:text-sm" onClick={onPreview}>Preview</button>
            <RefreshIcon
              className="h-6 w-6 ml-2 scale-up cursor-pointer dark:stroke-white"
              aria-label="refresh"
              role="button"
              onClick={onRefresh}
              tabIndex={0}
            />
          </div>
          <div className="flex flex-row items-center p-4">
            {puzzle && <Puzzle puzzle={puzzle} isDarkMode={isDarkMode} size={6} containerSize={8} />}
          </div>
          {puzzle.length > 0 &&
          <div className="flex flex-row items-center p-4">
            <button className="m-2 h-14 flex flex-row w-32 justify-center items-center text-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:text-sm" onClick={onShare}>Share
            <ShareIcon
              className="h-6 w-6 ml-2 scale-up cursor-pointer dark:stroke-white"
              aria-label="get-link"
            />
            </button>
            <button className="m-2 h-14 flex flex-row w-32 justify-center items-center text-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:text-sm" onClick={onOpen}>Open puzzle
            </button>
          </div>}
        </div>
      </BaseModal>
		)
}
